import React, { useState, useEffect, useCallback } from 'react';
import {
  Crown, BookOpen, ShieldCheck, AlertTriangle, UserPlus, Search, X,
  Users, Package, UserX, Layers, Settings2, UserMinus, FileText, Edit2,
} from 'lucide-react';
import RyanCuratedAccessPicker, { CuratedAccessValue, emptyCuratedAccess } from '../RyanCuratedAccessPicker';
import './RyanAdminAccounts.css';

// ─── TYPES ───────────────────────────────────────────────────────────────────

type Tab = 'overview' | 'vip' | 'editors';

interface OverviewData {
    totalUsers: number;
    withPurchase: number;
    withoutPurchase: number;
    activeProducts: number;
    accountTypes: { admins: number; editors: number; vip: number; common: number };
}

interface VipEntry {
    _id: string;
    userId: { _id: string; username: string; email: string } | null;
    isVip: boolean;
    isEditor: boolean;
    vipExpiresAt: string | null;
    createdAt: string;
    curatedAccessEnabled?: boolean;
    allowedSpaceIds?: string[];
}

interface EditorEntry {
    _id: string;
    userId: { _id: string; username: string; email: string } | null;
    isEditor: boolean;
    isVip: boolean;
    createdAt: string;
    curatedAccessEnabled?: boolean;
    allowedSpaceIds?: string[];
}

interface SearchUser {
    _id: string;
    username: string;
    email: string;
}

// ─── HELPERS ─────────────────────────────────────────────────────────────────

const tok = () => localStorage.getItem('accessToken') || '';

const fmtDate = (d: string | null | undefined) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const isExpired = (d: string | null | undefined) => {
    if (!d) return false;
    return new Date(d) < new Date();
};

const initials = (name: string | null | undefined) => {
    if (!name) return '?';
    return name.split(' ').map((p: string) => p[0]).join('').toUpperCase().slice(0, 2);
};

const toInputDate = (d: string | null | undefined) => {
    if (!d) return '';
    return new Date(d).toISOString().slice(0, 10);
};

const shiftDate = (base: string | null | undefined, days: number): string => {
    const d = base ? new Date(base) : new Date();
    if (d < new Date()) d.setTime(Date.now());
    d.setDate(d.getDate() + days);
    return d.toISOString();
};

const getExpColor = (exp: string | null) => {
    if (!exp) return '#d97706';
    const daysLeft = Math.ceil((new Date(exp).getTime() - Date.now()) / 86400000);
    if (daysLeft < 0) return '#dc2626';
    if (daysLeft < 30) return '#d97706';
    return '#16a34a';
};

const getExpLabel = (exp: string | null) => {
    if (!exp) return 'Vitalício';
    const expired = isExpired(exp);
    return (expired ? 'Expirou em ' : 'Expira em ') + fmtDate(exp);
};

const TAB_TITLES: Record<Tab, string> = {
    overview: 'Contas GRAIA',
    vip:      'Contas VIP',
    editors:  'Contas Editores',
};

const TAB_SUBTITLES: Record<Tab, string> = {
    overview: 'Distribuição de tipos de conta e alertas do sistema.',
    vip:      'Gerencie usuários com acesso VIP.',
    editors:  'Gerencie contas com permissão de editor.',
};

// ─── COUNT-UP ────────────────────────────────────────────────────────────────

const CountUp: React.FC<{ to: number; duration?: number }> = ({ to, duration = 900 }) => {
    const [val, setVal] = useState(0);
    useEffect(() => {
        if (to === 0) { setVal(0); return; }
        const start = Date.now();
        const tick = () => {
            const elapsed = Date.now() - start;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setVal(Math.round(eased * to));
            if (progress < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
    }, [to, duration]);
    return <>{val}</>;
};

// ─── COMPONENT ───────────────────────────────────────────────────────────────

interface RyanAdminAccountsProps {
    onHeaderChange: (title: string, subtitle: string) => void;
}

const RyanAdminAccounts: React.FC<RyanAdminAccountsProps> = ({ onHeaderChange }) => {
    const [activeTab, setActiveTab] = useState<Tab>('overview');

    useEffect(() => {
        onHeaderChange(TAB_TITLES[activeTab], TAB_SUBTITLES[activeTab]);
    }, [activeTab, onHeaderChange]);

    // ── Overview ──────────────────────────────────────────────────────────────
    const [overview, setOverview] = useState<OverviewData | null>(null);
    const [overviewLoading, setOverviewLoading] = useState(false);

    const fetchOverview = useCallback(async () => {
        setOverviewLoading(true);
        try {
            const r = await fetch('/api/ryan/admin/accounts/overview', {
                headers: { Authorization: `Bearer ${tok()}` },
            });
            const d = await r.json();
            setOverview(d);
        } catch { /* ignore */ }
        setOverviewLoading(false);
    }, []);

    // ── VIP ───────────────────────────────────────────────────────────────────
    const [vipList, setVipList] = useState<VipEntry[]>([]);
    const [vipLoading, setVipLoading] = useState(false);
    const [vipModal, setVipModal] = useState(false);
    const [vipTab, setVipTab] = useState<'create' | 'promote'>('create');
    const [vipCreate, setVipCreate] = useState({ username: '', email: '', password: '', expiresAt: '', lifetime: false });
    const [vipSearch, setVipSearch] = useState('');
    const [vipResults, setVipResults] = useState<SearchUser[]>([]);
    const [vipSelected, setVipSelected] = useState<SearchUser | null>(null);
    const [vipPromoteDate, setVipPromoteDate] = useState('');
    const [vipPromoteLifetime, setVipPromoteLifetime] = useState(false);
    const [vipManage, setVipManage] = useState<VipEntry | null>(null);
    const [vipManageTab, setVipManageTab] = useState<'extend' | 'shorten' | 'revoke' | 'fields'>('extend');
    const [vipManageDate, setVipManageDate] = useState('');
    const [vipManageLifetime, setVipManageLifetime] = useState(false);
    const [vipRevokeConfirm, setVipRevokeConfirm] = useState(false);
    const [vipSaving, setVipSaving] = useState(false);
    const [vipCurated, setVipCurated] = useState<CuratedAccessValue>(emptyCuratedAccess());
    const [vipCuratedSaving, setVipCuratedSaving] = useState(false);

    const fetchVip = useCallback(async () => {
        setVipLoading(true);
        try {
            const r = await fetch('/api/ryan/admin/accounts/vip', {
                headers: { Authorization: `Bearer ${tok()}` },
            });
            const d = await r.json();
            if (d.success) setVipList(d.users);
        } catch { /* ignore */ }
        setVipLoading(false);
    }, []);

    const searchUsers = useCallback(async (q: string, setter: (u: SearchUser[]) => void) => {
        try {
            const r = await fetch(`/api/ryan/admin/stats/users?search=${encodeURIComponent(q)}`, {
                headers: { Authorization: `Bearer ${tok()}` },
            });
            const d = await r.json();
            if (d.success) setter(d.users);
        } catch { /* ignore */ }
    }, []);

    useEffect(() => {
        const t = setTimeout(() => {
            if (vipModal && vipTab === 'promote') searchUsers(vipSearch, setVipResults);
        }, 350);
        return () => clearTimeout(t);
    }, [vipSearch, vipModal, vipTab, searchUsers]);

    const handleVipCreate = async () => {
        setVipSaving(true);
        try {
            const body: any = {
                username: vipCreate.username,
                email: vipCreate.email,
                password: vipCreate.password,
            };
            if (!vipCreate.lifetime && vipCreate.expiresAt) {
                body.expiresAt = new Date(vipCreate.expiresAt).toISOString();
            }
            const r = await fetch('/api/ryan/admin/accounts/create-vip', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
                body: JSON.stringify(body),
            });
            const d = await r.json();
            if (!r.ok) { alert(d.message || 'Erro ao criar'); setVipSaving(false); return; }
            setVipModal(false);
            setVipCreate({ username: '', email: '', password: '', expiresAt: '', lifetime: false });
            fetchVip();
        } catch { alert('Erro de rede'); }
        setVipSaving(false);
    };

    const handleVipPromote = async () => {
        if (!vipSelected) return;
        setVipSaving(true);
        try {
            const body: any = {
                expiresAt: vipPromoteLifetime ? null : (vipPromoteDate ? new Date(vipPromoteDate).toISOString() : null),
            };
            const r = await fetch(`/api/ryan/admin/accounts/${vipSelected._id}/grant-vip`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
                body: JSON.stringify(body),
            });
            if (!r.ok) { const d = await r.json(); alert(d.message || 'Erro'); setVipSaving(false); return; }
            setVipModal(false);
            setVipSelected(null);
            setVipSearch('');
            setVipResults([]);
            fetchVip();
        } catch { alert('Erro de rede'); }
        setVipSaving(false);
    };

    const handleVipManageSave = async () => {
        if (!vipManage?.userId?._id) return;
        setVipSaving(true);
        try {
            const body: any = {
                expiresAt: vipManageLifetime ? null : (vipManageDate ? new Date(vipManageDate).toISOString() : null),
            };
            const r = await fetch(`/api/ryan/admin/accounts/${vipManage.userId._id}/grant-vip`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
                body: JSON.stringify(body),
            });
            if (r.ok) { setVipManage(null); fetchVip(); }
        } catch { alert('Erro de rede'); }
        setVipSaving(false);
    };

    const handleVipShift = (days: number) => {
        setVipManageDate(toInputDate(shiftDate(vipManage?.vipExpiresAt, days)));
        setVipManageLifetime(false);
    };

    const handleVipRevoke = async () => {
        if (!vipManage?.userId?._id) return;
        setVipSaving(true);
        try {
            await fetch(`/api/ryan/admin/accounts/${vipManage.userId._id}/revoke-vip`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${tok()}` },
            });
            setVipManage(null);
            setVipRevokeConfirm(false);
            fetchVip();
        } catch { alert('Erro de rede'); }
        setVipSaving(false);
    };

    const openVipManage = (entry: VipEntry) => {
        setVipManage(entry);
        setVipManageTab('extend');
        setVipManageDate(toInputDate(entry.vipExpiresAt));
        setVipManageLifetime(!entry.vipExpiresAt);
        setVipRevokeConfirm(false);
        setVipCurated({
            curatedAccessEnabled: !!entry.curatedAccessEnabled,
            allowedSpaceIds: entry.allowedSpaceIds || [],
        });
    };

    const handleVipCuratedSave = async () => {
        if (!vipManage) return;
        setVipCuratedSaving(true);
        try {
            const r = await fetch(`/api/ryan/admin/accesses/${vipManage._id}/curated-access`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
                body: JSON.stringify(vipCurated),
            });
            if (r.ok) { setVipManage(null); fetchVip(); }
        } catch { alert('Erro de rede'); }
        setVipCuratedSaving(false);
    };

    // ── Editors ───────────────────────────────────────────────────────────────
    const [ediList, setEdiList] = useState<EditorEntry[]>([]);
    const [ediLoading, setEdiLoading] = useState(false);
    const [ediModal, setEdiModal] = useState(false);
    const [ediTab, setEdiTab] = useState<'create' | 'promote'>('create');
    const [ediCreate, setEdiCreate] = useState({ username: '', email: '', password: '' });
    const [ediSearch, setEdiSearch] = useState('');
    const [ediResults, setEdiResults] = useState<SearchUser[]>([]);
    const [ediSelected, setEdiSelected] = useState<SearchUser | null>(null);
    const [ediRevokeId, setEdiRevokeId] = useState<string | null>(null);
    const [ediSaving, setEdiSaving] = useState(false);
    const [ediManage, setEdiManage] = useState<EditorEntry | null>(null);
    const [ediCurated, setEdiCurated] = useState<CuratedAccessValue>(emptyCuratedAccess());
    const [ediCuratedSaving, setEdiCuratedSaving] = useState(false);

    const fetchEditors = useCallback(async () => {
        setEdiLoading(true);
        try {
            const r = await fetch('/api/ryan/admin/accounts/editors', {
                headers: { Authorization: `Bearer ${tok()}` },
            });
            const d = await r.json();
            if (d.success) setEdiList(d.users);
        } catch { /* ignore */ }
        setEdiLoading(false);
    }, []);

    useEffect(() => {
        const t = setTimeout(() => {
            if (ediModal && ediTab === 'promote') searchUsers(ediSearch, setEdiResults);
        }, 350);
        return () => clearTimeout(t);
    }, [ediSearch, ediModal, ediTab, searchUsers]);

    const handleEdiCreate = async () => {
        setEdiSaving(true);
        try {
            const r = await fetch('/api/ryan/admin/accounts/create-editor', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
                body: JSON.stringify(ediCreate),
            });
            const d = await r.json();
            if (!r.ok) { alert(d.message || 'Erro ao criar'); setEdiSaving(false); return; }
            setEdiModal(false);
            setEdiCreate({ username: '', email: '', password: '' });
            fetchEditors();
        } catch { alert('Erro de rede'); }
        setEdiSaving(false);
    };

    const handleEdiPromote = async () => {
        if (!ediSelected) return;
        setEdiSaving(true);
        try {
            const r = await fetch(`/api/ryan/admin/accounts/${ediSelected._id}/grant-editor`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${tok()}` },
            });
            if (!r.ok) { const d = await r.json(); alert(d.message || 'Erro'); setEdiSaving(false); return; }
            setEdiModal(false);
            setEdiSelected(null);
            setEdiSearch('');
            setEdiResults([]);
            fetchEditors();
        } catch { alert('Erro de rede'); }
        setEdiSaving(false);
    };

    const handleEdiRevoke = async (userId: string) => {
        if (!userId) return;
        setEdiSaving(true);
        try {
            await fetch(`/api/ryan/admin/accounts/${userId}/revoke-editor`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${tok()}` },
            });
            setEdiRevokeId(null);
            fetchEditors();
        } catch { alert('Erro de rede'); }
        setEdiSaving(false);
    };

    const openEdiManage = (entry: EditorEntry) => {
        setEdiManage(entry);
        setEdiCurated({
            curatedAccessEnabled: !!entry.curatedAccessEnabled,
            allowedSpaceIds: entry.allowedSpaceIds || [],
        });
    };

    const handleEdiCuratedSave = async () => {
        if (!ediManage) return;
        setEdiCuratedSaving(true);
        try {
            const r = await fetch(`/api/ryan/admin/accesses/${ediManage._id}/curated-access`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
                body: JSON.stringify(ediCurated),
            });
            if (r.ok) { setEdiManage(null); fetchEditors(); }
        } catch { alert('Erro de rede'); }
        setEdiCuratedSaving(false);
    };

    // ── Tab load ──────────────────────────────────────────────────────────────
    useEffect(() => {
        if (activeTab === 'overview') fetchOverview();
        if (activeTab === 'vip') fetchVip();
        if (activeTab === 'editors') fetchEditors();
    }, [activeTab, fetchOverview, fetchVip, fetchEditors]);

    // ─────────────────────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────────────────────

    return (
        <div className="raa-root">
            <div className="raa-tabs">
                {(['overview', 'vip', 'editors'] as Tab[]).map(t => (
                    <button
                        key={t}
                        className={`raa-tab${activeTab === t ? ' raa-tab--active' : ''}`}
                        onClick={() => setActiveTab(t)}
                    >
                        {TAB_TITLES[t]}
                    </button>
                ))}
            </div>

            {/* ── OVERVIEW ────────────────────────────────────────────────── */}
            {activeTab === 'overview' && (
                <div className="raa-pane">
                    {overviewLoading && <p className="raa-loading">Carregando...</p>}
                    {overview && (
                        <>
                            <div className="raa-kpi-grid">
                                <div className="raa-kpi" style={{ '--kpi-color': '#0D3B0E', '--kpi-bg': 'rgba(13,59,14,0.06)', '--kpi-border': 'rgba(13,59,14,0.2)' } as React.CSSProperties}>
                                    <div className="raa-kpi-top">
                                        <Users size={15} className="raa-kpi-icon" />
                                        <span className="raa-kpi-label">Usuários ativos</span>
                                    </div>
                                    <div className="raa-kpi-value"><CountUp to={overview.totalUsers} /></div>
                                </div>
                                <div className="raa-kpi" style={{ '--kpi-color': '#3b82f6', '--kpi-bg': 'rgba(59,130,246,0.07)', '--kpi-border': 'rgba(59,130,246,0.25)' } as React.CSSProperties}>
                                    <div className="raa-kpi-top">
                                        <Package size={15} className="raa-kpi-icon" />
                                        <span className="raa-kpi-label">Com produto</span>
                                    </div>
                                    <div className="raa-kpi-value"><CountUp to={overview.withPurchase} /></div>
                                </div>
                                <div className="raa-kpi" style={{ '--kpi-color': '#6b7280', '--kpi-bg': 'rgba(107,114,128,0.07)', '--kpi-border': 'rgba(107,114,128,0.22)' } as React.CSSProperties}>
                                    <div className="raa-kpi-top">
                                        <UserX size={15} className="raa-kpi-icon" />
                                        <span className="raa-kpi-label">Sem produto</span>
                                    </div>
                                    <div className="raa-kpi-value"><CountUp to={overview.withoutPurchase} /></div>
                                </div>
                                <div className="raa-kpi" style={{ '--kpi-color': '#8b5cf6', '--kpi-bg': 'rgba(139,92,246,0.07)', '--kpi-border': 'rgba(139,92,246,0.25)' } as React.CSSProperties}>
                                    <div className="raa-kpi-top">
                                        <Layers size={15} className="raa-kpi-icon" />
                                        <span className="raa-kpi-label">Produtos ativos</span>
                                    </div>
                                    <div className="raa-kpi-value"><CountUp to={overview.activeProducts} /></div>
                                </div>
                            </div>

                            <div className="raa-section raa-section--hero">
                                <h3 className="raa-section-title">Distribuição de tipos</h3>
                                {([
                                    { label: 'Admins',   count: overview.accountTypes.admins,  color: '#0D3B0E', bg: 'linear-gradient(90deg, #0D3B0E, #1a5c1b)' },
                                    { label: 'Editores', count: overview.accountTypes.editors, color: '#6366f1', bg: 'linear-gradient(90deg, #6366f1, #818cf8)' },
                                    { label: 'VIP',      count: overview.accountTypes.vip,     color: '#f59e0b', bg: 'linear-gradient(90deg, #f59e0b, #fbbf24)' },
                                    { label: 'Comum',    count: overview.accountTypes.common,  color: '#9ca3af', bg: 'linear-gradient(90deg, #9ca3af, #d1d5db)' },
                                ] as const).map(({ label, count, color, bg }) => {
                                    const pct = overview.totalUsers > 0
                                        ? Math.round((count / overview.totalUsers) * 100)
                                        : 0;
                                    return (
                                        <div key={label} className="raa-dist-row">
                                            <span className="raa-dist-label">{label}</span>
                                            <div className="raa-dist-bar-wrap">
                                                <div className="raa-dist-bar" style={{ width: `${Math.max(pct, 2)}%`, background: bg }} />
                                            </div>
                                            <span className="raa-dist-pct" style={{ color }}>{pct}%</span>
                                            <span className="raa-dist-count" style={{ color }}>{count}</span>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="raa-section">
                                <h3 className="raa-section-title">Alertas do sistema</h3>
                                <div className="raa-alert raa-alert--info">
                                    <ShieldCheck size={18} />
                                    <span>Monitoramento de acesso admin ativo. Tentativas suspeitas de escalação de privilégio serão reportadas aqui.</span>
                                </div>
                                {overview.withoutPurchase > 0 && (
                                    <div className="raa-alert raa-alert--warn">
                                        <AlertTriangle size={18} />
                                        <span>{overview.withoutPurchase} usuário(s) com acesso mas sem produto atribuído.</span>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* ── VIP ─────────────────────────────────────────────────────── */}
            {activeTab === 'vip' && (
                <div className="raa-pane">
                    <div className="raa-pane-header">
                        <span className="raa-pane-count">{vipList.length} VIPs</span>
                        <button className="raa-add-btn raa-add-btn--vip" onClick={() => { setVipModal(true); setVipTab('create'); }}>
                            <UserPlus size={15} /> Adicionar VIP
                        </button>
                    </div>

                    {vipLoading && <p className="raa-loading">Carregando...</p>}
                    {!vipLoading && vipList.length === 0 && <p className="raa-empty">Nenhum usuário VIP.</p>}

                    <div className="rvip-grid">
                        {vipList.map((entry, idx) => {
                            const u = entry.userId;
                            const exp = entry.vipExpiresAt;
                            const expired = isExpired(exp);
                            const expColor = getExpColor(exp);
                            return (
                                <div key={entry._id} className={`rvip-card${expired ? ' rvip-card--expired' : ''}`}
                                    style={{ animationDelay: `${idx * 60}ms` }}>
                                    <div className="rvip-card-top">
                                        <div className="rvip-avatar">{initials(u?.username)}</div>
                                        <div className="rvip-info">
                                            <span className="rvip-name">{u?.username || '—'}</span>
                                            <span className="rvip-email">{u?.email || entry._id}</span>
                                        </div>
                                        <span className={`rvip-badge${expired ? ' rvip-badge--expired' : ''}`}>
                                            <Crown size={11} />
                                            {expired ? 'Expirado' : 'VIP'}
                                        </span>
                                    </div>
                                    <div className="rvip-card-meta" style={{ color: expColor }}>
                                        {getExpLabel(exp)}
                                    </div>
                                    <button className="rvip-manage-btn" onClick={() => openVipManage(entry)}>
                                        <Settings2 size={14} /> Gerenciar
                                    </button>
                                </div>
                            );
                        })}
                    </div>

                    {/* Modal Criar/Promover VIP */}
                    {vipModal && (
                        <div className="raa-overlay" onClick={() => setVipModal(false)}>
                            <div className="raa-modal raa-modal--vip" onClick={e => e.stopPropagation()}>
                                <div className="raa-modal-stripe raa-modal-stripe--vip" />
                                <div className="raa-modal-header">
                                    <div className="raa-modal-header-inner">
                                        <Crown size={16} className="raa-modal-icon raa-modal-icon--vip" />
                                        <span>Adicionar VIP</span>
                                    </div>
                                    <button className="raa-modal-close" onClick={() => setVipModal(false)}><X size={16} /></button>
                                </div>
                                <div className="raa-modal-tabs raa-modal-tabs--vip">
                                    <button className={vipTab === 'create' ? 'active' : ''} onClick={() => setVipTab('create')}>Criar conta</button>
                                    <button className={vipTab === 'promote' ? 'active' : ''} onClick={() => setVipTab('promote')}>Promover existente</button>
                                </div>

                                {vipTab === 'create' && (
                                    <div className="raa-modal-body">
                                        <label className="raa-label">Usuário</label>
                                        <input className="raa-input raa-input--vip" placeholder="Nome de usuário" value={vipCreate.username} onChange={e => setVipCreate(p => ({ ...p, username: e.target.value }))} />
                                        <label className="raa-label">E-mail</label>
                                        <input className="raa-input raa-input--vip" placeholder="email@exemplo.com" value={vipCreate.email} onChange={e => setVipCreate(p => ({ ...p, email: e.target.value }))} />
                                        <label className="raa-label">Senha</label>
                                        <input className="raa-input raa-input--vip" type="password" placeholder="Senha inicial" value={vipCreate.password} onChange={e => setVipCreate(p => ({ ...p, password: e.target.value }))} />
                                        <div className="raa-row">
                                            <div className="raa-field">
                                                <label className="raa-label">Expira em</label>
                                                <input className="raa-input raa-input--vip" type="date" value={vipCreate.expiresAt} disabled={vipCreate.lifetime} onChange={e => setVipCreate(p => ({ ...p, expiresAt: e.target.value }))} />
                                            </div>
                                            <label className="raa-checkbox-label">
                                                <input type="checkbox" checked={vipCreate.lifetime} onChange={e => setVipCreate(p => ({ ...p, lifetime: e.target.checked }))} />
                                                Vitalício
                                            </label>
                                        </div>
                                        <button className="raa-submit-btn raa-submit-btn--vip" onClick={handleVipCreate} disabled={vipSaving}>
                                            {vipSaving ? 'Criando...' : 'Criar conta VIP'}
                                        </button>
                                    </div>
                                )}

                                {vipTab === 'promote' && (
                                    <div className="raa-modal-body">
                                        <label className="raa-label">Buscar usuário</label>
                                        <div className="raa-search-wrap">
                                            <Search size={14} className="raa-search-icon" />
                                            <input
                                                className="raa-search-input raa-input--vip"
                                                placeholder="Nome ou e-mail..."
                                                value={vipSearch}
                                                onChange={e => { setVipSearch(e.target.value); setVipSelected(null); }}
                                            />
                                        </div>
                                        {!vipSelected && vipResults.length > 0 && (
                                            <div className="raa-results">
                                                {vipResults.map(u => (
                                                    <div key={u._id} className="raa-result-row" onClick={() => setVipSelected(u)}>
                                                        <span className="raa-result-name">{u.username}</span>
                                                        <span className="raa-result-email">{u.email}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {vipSelected && (
                                            <div className="raa-selected-user raa-selected-user--vip">
                                                <span>{vipSelected.username} — {vipSelected.email}</span>
                                                <button onClick={() => { setVipSelected(null); setVipSearch(''); }}><X size={12} /></button>
                                            </div>
                                        )}
                                        <div className="raa-row">
                                            <div className="raa-field">
                                                <label className="raa-label">Expira em</label>
                                                <input className="raa-input raa-input--vip" type="date" value={vipPromoteDate} disabled={vipPromoteLifetime} onChange={e => setVipPromoteDate(e.target.value)} />
                                            </div>
                                            <label className="raa-checkbox-label">
                                                <input type="checkbox" checked={vipPromoteLifetime} onChange={e => setVipPromoteLifetime(e.target.checked)} />
                                                Vitalício
                                            </label>
                                        </div>
                                        <button className="raa-submit-btn raa-submit-btn--vip" onClick={handleVipPromote} disabled={!vipSelected || vipSaving}>
                                            {vipSaving ? 'Promovendo...' : 'Conceder VIP'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Modal Gerenciar VIP */}
                    {vipManage && (
                        <div className="raa-overlay" onClick={() => { setVipManage(null); setVipRevokeConfirm(false); }}>
                            <div className="raa-modal raa-modal--vip" onClick={e => e.stopPropagation()}>
                                <div className="raa-modal-stripe raa-modal-stripe--vip" />
                                <div className="raa-modal-header">
                                    <div className="raa-modal-header-inner">
                                        <Crown size={16} className="raa-modal-icon raa-modal-icon--vip" />
                                        <span>Gerenciar VIP — {vipManage.userId?.username}</span>
                                    </div>
                                    <button className="raa-modal-close" onClick={() => { setVipManage(null); setVipRevokeConfirm(false); }}><X size={16} /></button>
                                </div>
                                <div className="raa-modal-tabs raa-modal-tabs--vip">
                                    <button className={vipManageTab === 'extend'  ? 'active' : ''} onClick={() => setVipManageTab('extend')}>Estender</button>
                                    <button className={vipManageTab === 'shorten' ? 'active' : ''} onClick={() => setVipManageTab('shorten')}>Encurtar</button>
                                    <button className={vipManageTab === 'fields'  ? 'active' : ''} onClick={() => setVipManageTab('fields')}>Campos</button>
                                    <button className={`raa-tab-danger${vipManageTab === 'revoke' ? ' active' : ''}`} onClick={() => setVipManageTab('revoke')}>Revogar</button>
                                </div>
                                <div className="raa-modal-body">
                                    {vipManageTab !== 'fields' && (
                                        <p className="raa-current-exp">
                                            Atual: <strong>{vipManage.vipExpiresAt ? fmtDate(vipManage.vipExpiresAt) : 'Vitalício'}</strong>
                                        </p>
                                    )}

                                    {vipManageTab === 'fields' && (
                                        <>
                                            <RyanCuratedAccessPicker value={vipCurated} onChange={setVipCurated} />
                                            <button className="raa-submit-btn raa-submit-btn--vip" onClick={handleVipCuratedSave} disabled={vipCuratedSaving} style={{ marginTop: 12 }}>
                                                {vipCuratedSaving ? 'Salvando...' : 'Salvar seleção'}
                                            </button>
                                        </>
                                    )}

                                    {(vipManageTab === 'extend' || vipManageTab === 'shorten') && (
                                        <>
                                            <div className="raa-quick-btns">
                                                {vipManageTab === 'extend'
                                                    ? ([7, 30, 90, 180, 365] as const).map(d => (
                                                        <button key={d} className="raa-quick-btn raa-quick-btn--vip" onClick={() => handleVipShift(d)}>
                                                            +{d === 7 ? '7d' : d === 30 ? '1m' : d === 90 ? '3m' : d === 180 ? '6m' : '1a'}
                                                        </button>
                                                    ))
                                                    : ([7, 30, 90, 180] as const).map(d => (
                                                        <button key={d} className="raa-quick-btn raa-quick-btn--sub" onClick={() => handleVipShift(-d)}>
                                                            -{d === 7 ? '7d' : d === 30 ? '1m' : d === 90 ? '3m' : '6m'}
                                                        </button>
                                                    ))
                                                }
                                            </div>
                                            <div className="raa-row">
                                                <div className="raa-field">
                                                    <label className="raa-label">Nova data</label>
                                                    <input className="raa-input raa-input--vip" type="date" value={vipManageDate} disabled={vipManageLifetime} onChange={e => setVipManageDate(e.target.value)} />
                                                </div>
                                                <label className="raa-checkbox-label">
                                                    <input type="checkbox" checked={vipManageLifetime} onChange={e => setVipManageLifetime(e.target.checked)} />
                                                    Vitalício
                                                </label>
                                            </div>
                                            <button className="raa-submit-btn raa-submit-btn--vip" onClick={handleVipManageSave} disabled={vipSaving}>
                                                {vipSaving ? 'Salvando...' : 'Salvar'}
                                            </button>
                                        </>
                                    )}

                                    {vipManageTab === 'revoke' && (
                                        <>
                                            {!vipRevokeConfirm ? (
                                                <>
                                                    <p className="raa-revoke-warn">
                                                        Remover o VIP de <strong>{vipManage.userId?.username}</strong>? O acesso VIP será revogado imediatamente.
                                                    </p>
                                                    <button className="raa-danger-btn" onClick={() => setVipRevokeConfirm(true)}>
                                                        Revogar VIP
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <p className="raa-revoke-warn raa-revoke-warn--confirm">
                                                        Tem certeza? Esta ação remove o VIP imediatamente.
                                                    </p>
                                                    <div className="raa-danger-row">
                                                        <button className="raa-danger-btn" onClick={handleVipRevoke} disabled={vipSaving}>
                                                            {vipSaving ? 'Revogando...' : 'Confirmar remoção'}
                                                        </button>
                                                        <button className="raa-cancel-btn" onClick={() => setVipRevokeConfirm(false)}>
                                                            Cancelar
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ── EDITORS ─────────────────────────────────────────────────── */}
            {activeTab === 'editors' && (
                <div className="raa-pane">
                    <div className="raa-pane-header">
                        <span className="raa-pane-count">{ediList.length} editores</span>
                        <button className="raa-add-btn raa-add-btn--edi" onClick={() => { setEdiModal(true); setEdiTab('create'); }}>
                            <UserPlus size={15} /> Adicionar Editor
                        </button>
                    </div>

                    {ediLoading && <p className="raa-loading">Carregando...</p>}
                    {!ediLoading && ediList.length === 0 && <p className="raa-empty">Nenhum editor cadastrado.</p>}

                    <div className="redi-grid">
                        {ediList.map((entry, idx) => {
                            const u = entry.userId;
                            return (
                                <div key={entry._id} className="redi-card" style={{ animationDelay: `${idx * 60}ms` }}>
                                    <div className="redi-card-top">
                                        <div className="redi-avatar">{initials(u?.username)}</div>
                                        <div className="redi-info">
                                            <span className="redi-name">{u?.username || '—'}</span>
                                            <span className="redi-email">{u?.email || entry._id}</span>
                                        </div>
                                        <span className="redi-badge"><Edit2 size={11} /> Editor</span>
                                    </div>
                                    <div className="redi-permissions">
                                        <span className="redi-perm-chip">
                                            <BookOpen size={10} /> Flashcards
                                        </span>
                                        <span className="redi-perm-chip">
                                            <FileText size={10} /> Apostilas
                                        </span>
                                    </div>
                                    <button className="redi-manage-btn" onClick={() => openEdiManage(entry)}>
                                        <Settings2 size={13} /> Gerenciar campos
                                    </button>
                                    {ediRevokeId === entry._id ? (
                                        <div className="redi-revoke-confirm">
                                            <span>Confirmar remoção?</span>
                                            <button className="redi-revoke-yes" onClick={() => handleEdiRevoke(u?._id || '')} disabled={ediSaving}>Sim</button>
                                            <button className="redi-revoke-no" onClick={() => setEdiRevokeId(null)}>Não</button>
                                        </div>
                                    ) : (
                                        <button className="redi-revoke-btn" onClick={() => setEdiRevokeId(entry._id)}>
                                            <UserMinus size={13} /> Revogar
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Modal Criar/Promover Editor */}
                    {ediModal && (
                        <div className="raa-overlay" onClick={() => setEdiModal(false)}>
                            <div className="raa-modal raa-modal--edi" onClick={e => e.stopPropagation()}>
                                <div className="raa-modal-stripe raa-modal-stripe--edi" />
                                <div className="raa-modal-header">
                                    <div className="raa-modal-header-inner">
                                        <Edit2 size={16} className="raa-modal-icon raa-modal-icon--edi" />
                                        <span>Adicionar Editor</span>
                                    </div>
                                    <button className="raa-modal-close" onClick={() => setEdiModal(false)}><X size={16} /></button>
                                </div>
                                <div className="raa-modal-tabs raa-modal-tabs--edi">
                                    <button className={ediTab === 'create'  ? 'active' : ''} onClick={() => setEdiTab('create')}>Criar conta</button>
                                    <button className={ediTab === 'promote' ? 'active' : ''} onClick={() => setEdiTab('promote')}>Promover existente</button>
                                </div>

                                {ediTab === 'create' && (
                                    <div className="raa-modal-body">
                                        <label className="raa-label">Usuário</label>
                                        <input className="raa-input raa-input--edi" placeholder="Nome de usuário" value={ediCreate.username} onChange={e => setEdiCreate(p => ({ ...p, username: e.target.value }))} />
                                        <label className="raa-label">E-mail</label>
                                        <input className="raa-input raa-input--edi" placeholder="email@exemplo.com" value={ediCreate.email} onChange={e => setEdiCreate(p => ({ ...p, email: e.target.value }))} />
                                        <label className="raa-label">Senha</label>
                                        <input className="raa-input raa-input--edi" type="password" placeholder="Senha inicial" value={ediCreate.password} onChange={e => setEdiCreate(p => ({ ...p, password: e.target.value }))} />
                                        <button className="raa-submit-btn raa-submit-btn--edi" onClick={handleEdiCreate} disabled={ediSaving}>
                                            {ediSaving ? 'Criando...' : 'Criar conta Editor'}
                                        </button>
                                    </div>
                                )}

                                {ediTab === 'promote' && (
                                    <div className="raa-modal-body">
                                        <label className="raa-label">Buscar usuário</label>
                                        <div className="raa-search-wrap">
                                            <Search size={14} className="raa-search-icon" />
                                            <input
                                                className="raa-search-input raa-input--edi"
                                                placeholder="Nome ou e-mail..."
                                                value={ediSearch}
                                                onChange={e => { setEdiSearch(e.target.value); setEdiSelected(null); }}
                                            />
                                        </div>
                                        {!ediSelected && ediResults.length > 0 && (
                                            <div className="raa-results">
                                                {ediResults.map(u => (
                                                    <div key={u._id} className="raa-result-row" onClick={() => setEdiSelected(u)}>
                                                        <span className="raa-result-name">{u.username}</span>
                                                        <span className="raa-result-email">{u.email}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {ediSelected && (
                                            <div className="raa-selected-user raa-selected-user--edi">
                                                <span>{ediSelected.username} — {ediSelected.email}</span>
                                                <button onClick={() => { setEdiSelected(null); setEdiSearch(''); }}><X size={12} /></button>
                                            </div>
                                        )}
                                        <button className="raa-submit-btn raa-submit-btn--edi" onClick={handleEdiPromote} disabled={!ediSelected || ediSaving}>
                                            {ediSaving ? 'Promovendo...' : 'Conceder Editor'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Modal Gerenciar campos (Editor) */}
                    {ediManage && (
                        <div className="raa-overlay" onClick={() => setEdiManage(null)}>
                            <div className="raa-modal raa-modal--edi" onClick={e => e.stopPropagation()}>
                                <div className="raa-modal-stripe raa-modal-stripe--edi" />
                                <div className="raa-modal-header">
                                    <div className="raa-modal-header-inner">
                                        <Settings2 size={16} className="raa-modal-icon raa-modal-icon--edi" />
                                        <span>Campos — {ediManage.userId?.username}</span>
                                    </div>
                                    <button className="raa-modal-close" onClick={() => setEdiManage(null)}><X size={16} /></button>
                                </div>
                                <div className="raa-modal-body">
                                    <RyanCuratedAccessPicker value={ediCurated} onChange={setEdiCurated} />
                                    <button className="raa-submit-btn raa-submit-btn--edi" onClick={handleEdiCuratedSave} disabled={ediCuratedSaving} style={{ marginTop: 12 }}>
                                        {ediCuratedSaving ? 'Salvando...' : 'Salvar seleção'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default RyanAdminAccounts;
